/**
 * TsmGroupRankingReportModal
 *
 * Multi-location modal for the TSM Group Ranking Report. Always queries all 8
 * service-bearing locations; user picks month/year only. The report splits the
 * results into 4 sections (Domestic, Import, Express, Unassigned) — each
 * ranked independently.
 *
 * Mirrors the TsmRankingReportModal pattern; differs in the Excel export
 * (one worksheet per group, plus a single "By Location" sheet at the end).
 */

import React, { useMemo, useState } from 'react';
import { XMarkIcon, PlayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ReportDefinition } from '../../types/reports';
import { ReportExportButtons } from './ReportExportButtons';
import { printCurrentPage } from '../../utils/reportExport';
import { loadExcelJS } from '../../utils/loadExcelJS';
import { runWholesaleComparison, RunReportResponse } from '../../services/api';
import {
  TsmGroupRankingReport,
  TsmGroupRankingReportData,
  TsmGroupSection,
} from './renderers/TsmGroupRankingReport';

const ELIGIBLE_LOCATION_IDS = [1, 2, 3, 4, 5, 10, 11, 14];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface TsmGroupRankingReportModalProps {
  report: ReportDefinition;
  isOpen: boolean;
  onClose: () => void;
}

type ReportStatus = 'idle' | 'running' | 'complete' | 'error';

export const TsmGroupRankingReportModal: React.FC<TsmGroupRankingReportModalProps> = ({
  report,
  isOpen,
  onClose,
}) => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<RunReportResponse | null>(null);
  const [runningMessage, setRunningMessage] = useState('Generating report...');

  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = now.getFullYear(); y >= 2026; y--) years.push(y);
    return years;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isRetryableError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    return msg.includes('failed to fetch') || msg.includes('networkerror') ||
      msg.includes('timeout') || msg.includes('504') || msg.includes('503') ||
      msg.includes('network') || msg.includes('aborted') || msg.includes('timed out');
  };

  const handleRunReport = async () => {
    setStatus('running');
    setError(null);
    setReportData(null);
    setRunningMessage('Generating report...');

    const warmupTimer = setTimeout(() => {
      setRunningMessage('Reading Service Advisor data from DynamoDB...');
    }, 5000);

    const MAX_ATTEMPTS = 2;
    let lastError: unknown;

    try {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await runWholesaleComparison(
            report.id,
            ELIGIBLE_LOCATION_IDS,
            selectedYear,
            selectedMonth,
          );

          if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to generate report');
          }

          clearTimeout(warmupTimer);
          setReportData(response.data);
          setStatus('complete');
          return;
        } catch (err) {
          lastError = err;
          if (attempt < MAX_ATTEMPTS && isRetryableError(err)) {
            console.warn(`TSM Group Ranking attempt ${attempt} failed, retrying...`, err);
            setRunningMessage('Retrying...');
            continue;
          }
          throw err;
        }
      }
    } catch {
      clearTimeout(warmupTimer);
      setError(lastError instanceof Error ? lastError.message : 'Failed to generate report');
      setStatus('error');
    }
  };

  const handlePrintPDF = () => {
    printCurrentPage();
  };

  const formatLeaderValue = (metric: string, value: number): string => {
    switch (metric) {
      case 'openROCount': return value.toLocaleString('en-US');
      case 'oldestRoAgeDays': return value === 0 ? '—' : `${value} days`;
      case 'mtdCpScGross':
      case 'mtdTotalGross':
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
      case 'avgHoursCp': return `${value.toFixed(2)} hrs`;
      case 'cpElr':
        return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
      default: return String(value);
    }
  };

  /**
   * Write one section's worksheet. Reuses the header/coloring conventions from
   * the original TSM Ranking modal so the two Excel exports look the same.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const writeGroupSheet = (wb: any, section: TsmGroupSection, ExcelJS: any, reportMonth: TsmGroupRankingReportData['reportMonth'], asOf: string | null) => {
    const sheetName = section.group.length <= 28 ? section.group : section.group.slice(0, 28);
    const ws = wb.addWorksheet(sheetName, {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 9 }],
      properties: { defaultRowHeight: 16 },
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    ws.getCell('A1').value = `TSM Group Ranking — ${section.group}`;
    ws.getCell('A1').font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
    ws.mergeCells('A1:R1');
    ws.getCell('A2').value = `${reportMonth.monthName} ${reportMonth.year} · ${section.totalAdvisors} advisor${section.totalAdvisors === 1 ? '' : 's'} ranked within ${section.group}`;
    ws.getCell('A2').font = { size: 11, color: { argb: 'FF4B5563' } };
    ws.mergeCells('A2:R2');
    ws.getCell('A3').value = `Snapshot as of: ${asOf ? new Date(asOf).toLocaleString('en-US') : '—'}`;
    ws.getCell('A3').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
    ws.mergeCells('A3:R3');

    ws.getCell('A5').value = 'Metric Leaders';
    ws.getCell('A5').font = { size: 11, bold: true, color: { argb: 'FF1F2937' } };
    ws.getCell('A5').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
    ws.mergeCells('A5:R5');
    const leaderLine = section.metricLeaders
      .map((m) => {
        const winners = m.winners.map((w) => w.advisorName).join(', ');
        const valStr = m.winners[0] ? formatLeaderValue(m.metric, m.winners[0].value) : '—';
        return `${m.label}: ${winners || '—'} (${valStr})`;
      })
      .join(' · ');
    ws.getCell('A6').value = leaderLine || '—';
    ws.getCell('A6').font = { size: 9, color: { argb: 'FF4B5563' } };
    ws.getCell('A6').alignment = { wrapText: true, vertical: 'top' };
    ws.mergeCells('A6:R6');
    ws.getRow(6).height = 50;

    ws.getCell('A8').value = 'Ranking — click the dropdown arrows to sort or filter';
    ws.getCell('A8').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
    ws.mergeCells('A8:R8');

    const headerRow = ws.getRow(9);
    const headers = [
      '#', 'Advisor', 'Location',
      'Closed ROs', 'Open ROs', 'Open Rank',
      'Oldest RO Date', 'Oldest RO (days)', 'Oldest Rank',
      'CP+SC Gross', 'CP+SC Rank',
      'Avg Hrs/RO (CP)', 'Avg Hrs Rank',
      'Total Labor Gross', 'Total Rank',
      'CP ELR', 'ELR Rank',
      'Total Score',
    ];
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
      cell.alignment = { horizontal: i <= 2 ? 'left' : 'right', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        bottom: { style: 'medium', color: { argb: 'FF111827' } },
        left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
        right: { style: 'thin', color: { argb: 'FFD1D5DB' } },
      };
    });
    headerRow.height = 22;

    const total = section.rows.length;
    const q1 = Math.max(1, Math.ceil(total * 0.25));
    const q4 = Math.floor(total * 0.75);
    const rankFill = (rank: number) => {
      if (rank <= q1) return 'FFD1FAE5';
      if (rank > q4) return 'FFFFE4E6';
      return 'FFF3F4F6';
    };
    const rankFontColor = (rank: number) => {
      if (rank <= q1) return 'FF065F46';
      if (rank > q4) return 'FF9F1239';
      return 'FF374151';
    };

    section.rows.forEach((row, i) => {
      const r = ws.getRow(10 + i);
      r.values = [
        i + 1,
        row.advisorName,
        row.locationName,
        row.closedROCount, row.openROCount, row.rankOpenRos,
        row.oldestOpenRODate ? new Date(row.oldestOpenRODate) : null,
        row.oldestRoAgeDays,
        row.rankOldestRo,
        row.mtdCpScGross, row.rankCpScGross,
        row.avgHoursCp, row.rankAvgHoursCp,
        row.mtdTotalGross, row.rankTotalGross,
        row.cpElr, row.rankCpElr,
        row.totalScore,
      ];
      r.getCell(1).alignment = { horizontal: 'right' };
      r.getCell(1).font = { bold: true };
      r.getCell(2).alignment = { horizontal: 'left' };
      r.getCell(3).alignment = { horizontal: 'left' };
      for (let c = 4; c <= 18; c++) r.getCell(c).alignment = { horizontal: 'right' };

      r.getCell(7).numFmt = 'yyyy-mm-dd';
      r.getCell(8).numFmt = '0" d"';
      r.getCell(10).numFmt = '"$"#,##0';
      r.getCell(12).numFmt = '0.00" hrs"';
      r.getCell(14).numFmt = '"$"#,##0';
      r.getCell(16).numFmt = '"$"#,##0.00';

      const rankCols: [number, number][] = [
        [6, row.rankOpenRos],
        [9, row.rankOldestRo],
        [11, row.rankCpScGross],
        [13, row.rankAvgHoursCp],
        [15, row.rankTotalGross],
        [17, row.rankCpElr],
      ];
      rankCols.forEach(([col, rank]) => {
        const cell = r.getCell(col);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rankFill(rank) } };
        cell.font = { bold: true, color: { argb: rankFontColor(rank) } };
      });

      r.getCell(18).font = { bold: true };
      r.getCell(18).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };

      if (i % 2 === 1) {
        for (let c = 1; c <= 18; c++) {
          if ([6, 9, 11, 13, 15, 17, 18].includes(c)) continue;
          r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } };
        }
      }
    });

    if (section.rows.length > 0) {
      const lastRow = 9 + section.rows.length;
      ws.autoFilter = {
        from: { row: 9, column: 1 },
        to: { row: lastRow, column: 18 },
      };
    }

    const widths = [5, 24, 32, 11, 10, 9, 13, 13, 9, 14, 9, 14, 9, 17, 9, 12, 9, 11];
    widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });

    return ws;
  };

  const handleExportExcel = async () => {
    if (!reportData?.data) return;
    const data = reportData.data as TsmGroupRankingReportData;

    const ExcelJS = await loadExcelJS();
    const wb = new ExcelJS.Workbook();
    wb.creator = "Darling's Dashboard";
    wb.created = new Date();

    // Four group worksheets (Domestic first, Unassigned last).
    writeGroupSheet(wb, data.groups.domestic, ExcelJS, data.reportMonth, data.asOf);
    writeGroupSheet(wb, data.groups.import, ExcelJS, data.reportMonth, data.asOf);
    writeGroupSheet(wb, data.groups.express, ExcelJS, data.reportMonth, data.asOf);
    writeGroupSheet(wb, data.groups.unassigned, ExcelJS, data.reportMonth, data.asOf);

    // Per-location aggregate sheet (identical layout to TSM Ranking export).
    if (data.locationRows && data.locationRows.length > 0) {
      const wsLoc = wb.addWorksheet('By Location', {
        views: [{ state: 'frozen', xSplit: 0, ySplit: 5 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });

      wsLoc.getCell('A1').value = 'TSM Group Ranking — By Location';
      wsLoc.getCell('A1').font = { size: 16, bold: true, color: { argb: 'FF1F2937' } };
      wsLoc.mergeCells('A1:Q1');
      wsLoc.getCell('A2').value = `${data.reportMonth.monthName} ${data.reportMonth.year} · ${data.locationRows.length} locations`;
      wsLoc.getCell('A2').font = { size: 11, color: { argb: 'FF4B5563' } };
      wsLoc.mergeCells('A2:Q2');
      wsLoc.getCell('A4').value = 'Click the column dropdown arrows in Excel to sort or filter';
      wsLoc.getCell('A4').font = { size: 10, italic: true, color: { argb: 'FF6B7280' } };
      wsLoc.mergeCells('A4:Q4');

      const locHeaders = [
        '#', 'Location', '# TSMs',
        'Closed ROs', 'Open ROs', 'Open Rank',
        'Oldest RO (days)', 'Oldest Rank',
        'Avg Hrs/RO (CP+SC)', 'Avg Hrs Rank',
        'CP ELR', 'ELR Rank',
        'Controllable Exp %', 'Ctrl Exp Rank',
        'Trend vs Proj %', 'Variance Rank',
        'Total Score',
      ];
      const locHeaderRow = wsLoc.getRow(5);
      locHeaders.forEach((h, i) => {
        const cell = locHeaderRow.getCell(i + 1);
        cell.value = h;
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF374151' } };
        cell.alignment = { horizontal: i <= 1 ? 'left' : 'right', vertical: 'middle' };
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF111827' } } };
      });
      locHeaderRow.height = 22;

      const locTotal = data.locationRows.length;
      const locQ1 = Math.max(1, Math.ceil(locTotal * 0.25));
      const locQ4 = Math.floor(locTotal * 0.75);
      const locRankFill = (rank: number) => {
        if (rank <= locQ1) return 'FFD1FAE5';
        if (rank > locQ4) return 'FFFFE4E6';
        return 'FFF3F4F6';
      };
      const locRankFontColor = (rank: number) => {
        if (rank <= locQ1) return 'FF065F46';
        if (rank > locQ4) return 'FF9F1239';
        return 'FF374151';
      };

      data.locationRows.forEach((lr, i) => {
        const r = wsLoc.getRow(6 + i);
        r.values = [
          i + 1,
          lr.locationName,
          lr.advisorCount,
          lr.closedROCount, lr.openROCount, lr.rankOpenRos,
          lr.oldestRoAgeDays, lr.rankOldestRo,
          lr.avgHoursCp, lr.rankAvgHoursCp,
          lr.cpElr, lr.rankCpElr,
          lr.controllableExpensePct / 100, lr.rankControllableExpense,
          lr.variancePct / 100, lr.rankVariancePct,
          lr.totalScore,
        ];
        r.getCell(1).font = { bold: true };
        r.getCell(1).alignment = { horizontal: 'right' };
        r.getCell(2).alignment = { horizontal: 'left' };
        for (let c = 3; c <= 17; c++) r.getCell(c).alignment = { horizontal: 'right' };

        r.getCell(7).numFmt = '0" d"';
        r.getCell(9).numFmt = '0.00" hrs"';
        r.getCell(11).numFmt = '"$"#,##0.00';
        r.getCell(13).numFmt = '0.0%';
        r.getCell(15).numFmt = '+0.0%;-0.0%;0.0%';

        const ranks: [number, number][] = [
          [6, lr.rankOpenRos], [8, lr.rankOldestRo], [10, lr.rankAvgHoursCp],
          [12, lr.rankCpElr], [14, lr.rankControllableExpense], [16, lr.rankVariancePct],
        ];
        ranks.forEach(([col, rank]) => {
          const cell = r.getCell(col);
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: locRankFill(rank) } };
          cell.font = { bold: true, color: { argb: locRankFontColor(rank) } };
        });
        r.getCell(17).font = { bold: true };
        r.getCell(17).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };

        if (i % 2 === 1) {
          for (let c = 1; c <= 17; c++) {
            if ([6, 8, 10, 12, 14, 16, 17].includes(c)) continue;
            r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFAFAFA' } };
          }
        }
      });

      wsLoc.autoFilter = {
        from: { row: 5, column: 1 },
        to: { row: 5 + data.locationRows.length, column: 17 },
      };

      const locWidths = [5, 34, 9, 11, 10, 9, 13, 9, 14, 9, 12, 9, 16, 9, 14, 9, 11];
      locWidths.forEach((w, i) => { wsLoc.getColumn(i + 1).width = w; });
    }

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TSM-Group-Ranking-${MONTHS[selectedMonth - 1]}-${selectedYear}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStatus('idle');
    setError(null);
    setReportData(null);
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (!isOpen) return null;

  const hasResults = status === 'complete';

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center p-4 print:p-0 print:overflow-visible print:static"
      onClick={handleBackdropClick}
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity print:hidden" />
      <div className="relative max-w-7xl w-full max-h-[95vh] bg-white rounded-lg shadow-xl flex flex-col print:max-w-none print:max-h-none print:rounded-none print:shadow-none">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between print:border-b-2">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{report.title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Service Advisor leaderboard split into Domestic / Import / Express — one page per group, printable for posting
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 print:hidden"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 print:overflow-visible">
          <div className="mb-6 print:hidden">
            <div className="flex flex-wrap items-end gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                <div className="flex gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                    disabled={status === 'running'}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                  >
                    {MONTHS.map((name, i) => (
                      <option key={i + 1} value={i + 1}>{name}</option>
                    ))}
                  </select>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    disabled={status === 'running'}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm"
                  >
                    {yearOptions.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={handleRunReport}
                disabled={status === 'running'}
                className={`
                  inline-flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors
                  ${status === 'running'
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-700'
                  }
                `}
              >
                {status === 'running' ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {runningMessage}
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4 mr-2" />
                    Run Report
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Buckets all Service Advisors across the 8 service-bearing locations (excludes Loc 12 &amp; 44 — Body Shop)
              into Domestic / Import / Express based on the Admin-set assignments in
              {' '}<span className="font-semibold">Configuration → TSM Groups</span>. Unassigned advisors show in a 4th section.
            </p>
          </div>

          <div className="border-t border-gray-200 mb-6 print:hidden" />

          <div id="report-print-content">
            <div className="hidden print:block mb-4">
              <h1 className="text-xl font-bold">TSM Group Ranking Report</h1>
              <p className="text-sm text-gray-600">
                {MONTHS[selectedMonth - 1]} {selectedYear}
              </p>
            </div>

            {status === 'idle' && (
              <div className="text-center py-12">
                <DocumentChartBarIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">
                  Pick a month and click "Run Report" to generate the Domestic / Import / Express leaderboards.
                </p>
              </div>
            )}

            {status === 'running' && (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-orange-600 mb-4" />
                <p className="text-gray-500">{runningMessage}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error || 'An error occurred while generating the report.'}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                >
                  Try again
                </button>
              </div>
            )}

            {status === 'complete' && reportData && (
              <TsmGroupRankingReport data={reportData.data as TsmGroupRankingReportData} />
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between print:hidden">
          <ReportExportButtons
            onPrintPDF={handlePrintPDF}
            onExportExcel={handleExportExcel}
            disabled={!hasResults}
          />
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
